import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import dayjs from 'dayjs';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import {
  UtmMetricTotals,
  UtmDailySourceData,
  UtmDailyRecord,
  UtmDailyAffiliateData,
  UtmDailyCanalData,
} from '../../../interfaces/utm';
import { UtmMetricsService } from '../../../services/utm-metrics.service';
import { UtmSegmentosService } from '../../../services/utm-segmentos.service';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'app-market',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    TooltipModule,
    SkeletonModule,
  ],
  templateUrl: './market.component.html',
  styleUrls: ['./market.component.scss'],
})
export class MarketComponent implements OnInit, OnDestroy {
  // â”€â”€ Daterange â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  selectedDate: { startDate: Date; endDate: Date } = {
    startDate: dayjs().startOf('month').toDate(),
    endDate: dayjs().toDate()
  };
  startDate: string = dayjs().startOf('month').format('YYYY-MM-DD');
  endDate: string = dayjs().format('YYYY-MM-DD');

  // â”€â”€ Filters â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  selectedSegmentoId = '';
  segmentoNome = '';

  // â”€â”€ Dynamic Content Based on Route â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  pageTitle = 'Market';
  pageIcon = 'fa-solid fa-store';
  pageDescription = 'Métricas de campanha por segmento de mercado';

  // â”€â”€ Data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  dailySources: UtmDailySourceData[] = [];
  totalDailyRows: UtmDailyRecord[] = [];
  totalExpanded = false;
  utmAfiliadosId = '';
  // â"€â"€ Admin Flow Data â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  isAdminView = false;
  affiliateGroups: UtmDailyAffiliateData[] = [];
  expandedAffiliates: Set<string> = new Set();
  activeAffiliateTab: Map<string, string> = new Map();
  // ── Canal Data ────────────────────────────────────────
  canalGroups: UtmDailyCanalData[] = [];
  expandedCanals: Set<string> = new Set();
  activeCanalTab: Map<string, string> = new Map();
  expandedAffiliateCanals: Set<string> = new Set();
  activeAffiliateCanalTab: Map<string, string> = new Map();
  // Sort state
  sortField: keyof UtmDailyRecord | '' = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  private readonly sourceColors = [
    '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f97316',
    '#ec4899', '#6366f1', '#14b8a6', '#f43f5e', '#84cc16',
  ];

  // â”€â”€ Summary Totals â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  grandTotals: UtmMetricTotals = this.emptyTotals();

  // â”€â”€ State â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  loading = false;
  loaded = false;

  private destroy$ = new Subject<void>();

  private metricsService = inject(UtmMetricsService);
  private segmentosService = inject(UtmSegmentosService);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);

  ngOnInit(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/telemarketing/')) {
      this.pageTitle = 'Telemarketing';
      this.pageIcon = 'fa-solid fa-headset';
      this.pageDescription = 'Métricas de campanha por segmento de telemarketing';
    }

    // Load user to get utm_afiliados_id, then subscribe to route params
    this.userService.me().pipe(
      takeUntil(this.destroy$),
      switchMap(userResponse => {
        if (userResponse.success) {
          this.utmAfiliadosId = (userResponse.data as any)?.utm_afiliados_id || '';
        }
        return this.route.paramMap;
      })
    ).subscribe((params) => {
      this.selectedSegmentoId = params.get('segmentoId') || '';
      if (this.selectedSegmentoId) {
        this.segmentoNome = this.segmentosService.getSegmentoNomeById(this.selectedSegmentoId);
        this.selectedDate = {
          startDate: dayjs().startOf('month').toDate(),
          endDate: dayjs().toDate(),
        };
        this.startDate = dayjs().startOf('month').format('YYYY-MM-DD');
        this.endDate = dayjs().format('YYYY-MM-DD');
        this.loadMetrics();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // â”€â”€ Date Change â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  onDateChange(): void {
    if (this.selectedDate.startDate && this.selectedDate.endDate) {
      this.startDate = dayjs(this.selectedDate.startDate).format('YYYY-MM-DD');
      this.endDate = dayjs(this.selectedDate.endDate).format('YYYY-MM-DD');
    }
  }

  // â”€â”€ Load Metrics â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  loadMetrics(): void {
    if (!this.selectedSegmentoId || !this.startDate || !this.endDate) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione um segmento e período para consultar.'
      });
      return;
    }

    if (!this.utmAfiliadosId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Seu usuário não possui ID de afiliado configurado.'
      });
      return;
    }

    // Detectar se é visualização admin
    this.isAdminView = this.utmAfiliadosId.toLowerCase() === 'all';

    this.loading = true;
    this.loaded = false;
    this.dailySources = [];
    this.totalDailyRows = [];
    this.totalExpanded = false;
    this.affiliateGroups = [];
    this.expandedAffiliates = new Set();
    this.activeAffiliateTab = new Map();
    this.canalGroups = [];
    this.expandedCanals = new Set();
    this.activeCanalTab = new Map();
    this.expandedAffiliateCanals = new Set();
    this.activeAffiliateCanalTab = new Map();

    this.metricsService.queryDaily({
      utm_afiliados_id: this.utmAfiliadosId,
      start_date: this.startDate,
      end_date: this.endDate,
      segmento_id: this.selectedSegmentoId,
    }).subscribe({
      next: (res: any) => {
        // Verificar se é resposta de admin através da estrutura dos dados
        const isAdminData = Array.isArray(res.data) && 
                           res.data.length > 0 && 
                           res.data[0].hasOwnProperty('afiliado_id');
        
        // Se data estiver vazio mas foi enviado "all", ainda é admin
        const isAdminQuery = this.utmAfiliadosId.toLowerCase() === 'all';
        
        if (isAdminData || isAdminQuery) {
          // Fluxo Admin: dados agrupados por afiliado
          this.processAdminResponse(res);
        } else {
          // Fluxo Afiliado: dados direto por source
          this.processAffiliateResponse(res);
        }
        this.loading = false;
        this.loaded = true;
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: err?.error?.message || 'Erro ao consultar métricas UTM.'
        });
        this.loading = false;
      },
    });
  }

  // â"€â"€ Process Admin Response â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  private processAdminResponse(res: any): void {
    this.affiliateGroups = res.data || [];
    this.isAdminView = true;
    
    // Processar cada afiliado e seus canais
    this.affiliateGroups.forEach(affiliate => {
      (affiliate.canais || []).forEach(canal => {
        canal.sources.forEach(s =>
          s.dias.sort((a, b) => this.parseDateStr(a.data) - this.parseDateStr(b.data))
        );
        const key = `${affiliate.afiliado_id}::${canal.canal_id}`;
        this.activeAffiliateCanalTab.set(key, 'total');
      });
    });

    // Computar totais gerais a partir de todos os afiliados
    this.computeGrandTotalsFromAffiliates();
    
    // Construir linhas diárias totais consolidadas
    this.buildTotalDailyRowsFromAffiliates();
  }

  private computeGrandTotalsFromAffiliates(): void {
    this.grandTotals = this.emptyTotals();
    for (const affiliate of this.affiliateGroups) {
      for (const canal of (affiliate.canais || [])) {
        for (const source of canal.sources) {
          const t = source.totais;
          this.grandTotals.valor_depositos += t.valor_depositos || 0;
          this.grandTotals.qtd_depositos += t.qtd_depositos || 0;
          this.grandTotals.valor_saques += t.valor_saques || 0;
          this.grandTotals.qtd_saques += t.qtd_saques || 0;
          this.grandTotals.qtd_ftd += t.qtd_ftd || 0;
          this.grandTotals.valor_ftd += t.valor_ftd || 0;
          this.grandTotals.qtd_novos_usuarios += t.qtd_novos_usuarios || 0;
          this.grandTotals.investimento += t.investimento || 0;
          this.grandTotals.ggr_acumulado += t.ggr_acumulado || 0;
          this.grandTotals.volume = (this.grandTotals.volume || 0) + (t.volume || 0);
        }
      }
    }
    this.calcDerivedTotals(this.grandTotals);
  }

  private buildTotalDailyRowsFromAffiliates(): void {
    const allSources: UtmDailySourceData[] = [];
    for (const affiliate of this.affiliateGroups) {
      for (const canal of (affiliate.canais || [])) {
        allSources.push(...canal.sources);
      }
    }
    this.totalDailyRows = this.aggregateDailyRows(allSources);
  }

  // â"€â"€ Process Affiliate Response â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  private processAffiliateResponse(res: any): void {
    this.canalGroups = res.data || [];
    this.canalGroups.forEach(canal => {
      canal.sources.forEach(s =>
        s.dias.sort((a, b) => this.parseDateStr(a.data) - this.parseDateStr(b.data))
      );
      this.activeCanalTab.set(canal.canal_id, 'total');
    });
    this.dailySources = [];
    for (const canal of this.canalGroups) {
      this.dailySources.push(...canal.sources);
    }
    this.computeGrandTotalsFromSources();
    this.buildTotalDailyRows();
  }

  // â”€â”€ Build Data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  private computeGrandTotalsFromSources(): void {
    this.grandTotals = this.emptyTotals();
    for (const source of this.dailySources) {
      const t = source.totais;
      this.grandTotals.valor_depositos += t.valor_depositos || 0;
      this.grandTotals.qtd_depositos += t.qtd_depositos || 0;
      this.grandTotals.valor_saques += t.valor_saques || 0;
      this.grandTotals.qtd_saques += t.qtd_saques || 0;
      this.grandTotals.qtd_ftd += t.qtd_ftd || 0;
      this.grandTotals.valor_ftd += t.valor_ftd || 0;
      this.grandTotals.qtd_novos_usuarios += t.qtd_novos_usuarios || 0;
      this.grandTotals.investimento += t.investimento || 0;
      this.grandTotals.ggr_acumulado += t.ggr_acumulado || 0;
      this.grandTotals.volume = (this.grandTotals.volume || 0) + (t.volume || 0);
    }
    this.calcDerivedTotals(this.grandTotals);
  }

  private buildTotalDailyRows(): void {
    this.totalDailyRows = this.aggregateDailyRows(this.dailySources);
  }

  private aggregateDailyRows(sources: UtmDailySourceData[]): UtmDailyRecord[] {
    const map = new Map<string, UtmDailyRecord>();

    for (const source of sources) {
      for (const dia of source.dias) {
        if (!map.has(dia.data)) {
          map.set(dia.data, {
            utm_source: 'Total',
            data: dia.data,
            valor_depositos: 0,
            qtd_depositos: 0,
            valor_saques: 0,
            qtd_saques: 0,
            qtd_ftd: 0,
            valor_ftd: 0,
            qtd_novos_usuarios: 0,
            ggr_acumulado: 0,
            passagem: 0,
            ticket_ftd: 0,
            ticket_dep: 0,
            retencao: 0,
          });
        }
        const row = map.get(dia.data)!;
        row.valor_depositos += dia.valor_depositos || 0;
        row.qtd_depositos += dia.qtd_depositos || 0;
        row.valor_saques += dia.valor_saques || 0;
        row.qtd_saques += dia.qtd_saques || 0;
        row.qtd_ftd += dia.qtd_ftd || 0;
        row.valor_ftd += dia.valor_ftd || 0;
        row.qtd_novos_usuarios += dia.qtd_novos_usuarios || 0;
        row.ggr_acumulado += dia.ggr_acumulado || 0;
      }
    }

    for (const row of map.values()) {
      row.passagem = row.qtd_novos_usuarios ? row.qtd_ftd / row.qtd_novos_usuarios : 0;
      row.ticket_ftd = row.qtd_ftd ? row.valor_ftd / row.qtd_ftd : 0;
      row.ticket_dep = row.qtd_depositos ? row.valor_depositos / row.qtd_depositos : 0;
      row.retencao = row.valor_ftd ? row.valor_depositos / row.valor_ftd : 0;
    }

    return Array.from(map.values()).sort(
      (a, b) => this.parseDateStr(a.data) - this.parseDateStr(b.data)
    );
  }
  // Accordion / Groups

  getGroupColor(index: number): string {
    return this.sourceColors[index % this.sourceColors.length];
  }

  expandAll(): void {
    this.totalExpanded = true;
    if (this.isAdminView) {
      this.affiliateGroups.forEach(a => {
        this.expandedAffiliates.add(a.afiliado_id);
        (a.canais || []).forEach(c => this.expandedAffiliateCanals.add(`${a.afiliado_id}::${c.canal_id}`));
      });
    } else {
      this.canalGroups.forEach(c => this.expandedCanals.add(c.canal_id));
    }
  }

  collapseAll(): void {
    this.totalExpanded = false;
    if (this.isAdminView) {
      this.expandedAffiliates.clear();
      this.expandedAffiliateCanals.clear();
    } else {
      this.expandedCanals.clear();
    }
  }

  // â"€â"€ Admin Affiliate Controls â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  isAffiliateExpanded(affiliateId: string): boolean {
    return this.expandedAffiliates.has(affiliateId);
  }

  toggleAffiliate(affiliateId: string): void {
    if (this.expandedAffiliates.has(affiliateId)) {
      this.expandedAffiliates.delete(affiliateId);
    } else {
      this.expandedAffiliates.add(affiliateId);
    }
  }

  getAffiliateTotals(affiliate: UtmDailyAffiliateData): UtmMetricTotals {
    const totals = this.emptyTotals();
    for (const canal of (affiliate.canais || [])) {
      for (const source of canal.sources) {
        const t = source.totais;
        totals.valor_depositos += t.valor_depositos || 0;
        totals.qtd_depositos += t.qtd_depositos || 0;
        totals.valor_saques += t.valor_saques || 0;
        totals.qtd_saques += t.qtd_saques || 0;
        totals.qtd_ftd += t.qtd_ftd || 0;
        totals.valor_ftd += t.valor_ftd || 0;
        totals.qtd_novos_usuarios += t.qtd_novos_usuarios || 0;
        totals.investimento += t.investimento || 0;
        totals.ggr_acumulado += t.ggr_acumulado || 0;
        totals.volume = (totals.volume || 0) + (t.volume || 0);
      }
    }
    this.calcDerivedTotals(totals);
    return totals;
  }

  getAffiliateDailyRows(affiliate: UtmDailyAffiliateData): UtmDailyRecord[] {
    const allSources: UtmDailySourceData[] = [];
    for (const canal of (affiliate.canais || [])) {
      allSources.push(...canal.sources);
    }
    return this.aggregateDailyRows(allSources);
  }

  getAffiliateSourcesCount(affiliate: UtmDailyAffiliateData): number {
    let count = 0;
    for (const canal of (affiliate.canais || [])) {
      count += canal.sources.length;
    }
    return count;
  }

  // ── Canal Controls ──────────────────────────────────────

  isCanalExpanded(canalId: string): boolean {
    return this.expandedCanals.has(canalId);
  }

  toggleCanal(canalId: string): void {
    if (this.expandedCanals.has(canalId)) {
      this.expandedCanals.delete(canalId);
    } else {
      this.expandedCanals.add(canalId);
    }
  }

  getCanalActiveTab(canalId: string): string {
    return this.activeCanalTab.get(canalId) || 'total';
  }

  setCanalActiveTab(canalId: string, tab: string): void {
    this.activeCanalTab.set(canalId, tab);
  }

  getCanalTabSource(canal: UtmDailyCanalData): UtmDailySourceData | undefined {
    const tab = this.activeCanalTab.get(canal.canal_id);
    return canal.sources.find(s => s.utm_source === tab);
  }

  getCanalTotals(canal: UtmDailyCanalData): UtmMetricTotals {
    const totals = this.emptyTotals();
    for (const source of canal.sources) {
      const t = source.totais;
      totals.valor_depositos += t.valor_depositos || 0;
      totals.qtd_depositos += t.qtd_depositos || 0;
      totals.valor_saques += t.valor_saques || 0;
      totals.qtd_saques += t.qtd_saques || 0;
      totals.qtd_ftd += t.qtd_ftd || 0;
      totals.valor_ftd += t.valor_ftd || 0;
      totals.qtd_novos_usuarios += t.qtd_novos_usuarios || 0;
      totals.investimento += t.investimento || 0;
      totals.ggr_acumulado += t.ggr_acumulado || 0;
      totals.volume = (totals.volume || 0) + (t.volume || 0);
    }
    this.calcDerivedTotals(totals);
    return totals;
  }

  getCanalDailyRows(canal: UtmDailyCanalData): UtmDailyRecord[] {
    return this.aggregateDailyRows(canal.sources);
  }

  // ── Admin Affiliate-Canal Controls ─────────────────────

  isAffiliateCanalExpanded(affiliateId: string, canalId: string): boolean {
    return this.expandedAffiliateCanals.has(`${affiliateId}::${canalId}`);
  }

  toggleAffiliateCanal(affiliateId: string, canalId: string): void {
    const key = `${affiliateId}::${canalId}`;
    if (this.expandedAffiliateCanals.has(key)) {
      this.expandedAffiliateCanals.delete(key);
    } else {
      this.expandedAffiliateCanals.add(key);
    }
  }

  getAffiliateCanalActiveTab(affiliateId: string, canalId: string): string {
    return this.activeAffiliateCanalTab.get(`${affiliateId}::${canalId}`) || 'total';
  }

  setAffiliateCanalActiveTab(affiliateId: string, canalId: string, tab: string): void {
    this.activeAffiliateCanalTab.set(`${affiliateId}::${canalId}`, tab);
  }

  getAffiliateCanalTabSource(affiliate: UtmDailyAffiliateData, canal: UtmDailyCanalData): UtmDailySourceData | undefined {
    const tab = this.activeAffiliateCanalTab.get(`${affiliate.afiliado_id}::${canal.canal_id}`);
    return canal.sources.find(s => s.utm_source === tab);
  }

  private parseDateStr(s: string): number {
    const [d, m, y] = s.split('-');
    return new Date(+y, +m - 1, +d).getTime();
  }

  // Sort
  toggleSort(field: keyof UtmDailyRecord): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = field === 'data' ? 'asc' : 'desc';
    }
    this.applySortAll();
  }

  private applySortAll(): void {
    const field = this.sortField;
    if (!field) return;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    const comparator = (a: UtmDailyRecord, b: UtmDailyRecord) => {
      if (field === 'data') {
        return (this.parseDateStr(a.data) - this.parseDateStr(b.data)) * dir;
      }
      if (field === 'utm_source') {
        return a.utm_source.localeCompare(b.utm_source) * dir;
      }
      return (((a[field] as number) || 0) - ((b[field] as number) || 0)) * dir;
    };
    this.totalDailyRows.sort(comparator);
    if (this.isAdminView) {
      for (const affiliate of this.affiliateGroups) {
        for (const canal of (affiliate.canais || [])) {
          for (const s of canal.sources) {
            s.dias.sort(comparator);
          }
        }
      }
    } else {
      for (const canal of this.canalGroups) {
        for (const s of canal.sources) {
          s.dias.sort(comparator);
        }
      }
    }
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) return 'fa-sort';
    return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }


  // â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  getTotalSourcesCount(): number {
    if (this.isAdminView) {
      let count = 0;
      for (const affiliate of this.affiliateGroups) {
        for (const canal of (affiliate.canais || [])) {
          count += canal.sources.length;
        }
      }
      return count;
    }
    return this.dailySources.length;
  }
  get isVolumeSegment(): boolean {
    if (!this.selectedSegmentoId) return false;
    const id = this.selectedSegmentoId.toLowerCase();
    return id.includes('sms') || id.includes('whatsapp');
  }

  private emptyTotals(): UtmMetricTotals {
    return {
      valor_depositos: 0,
      qtd_depositos: 0,
      valor_saques: 0,
      qtd_saques: 0,
      qtd_ftd: 0,
      valor_ftd: 0,
      qtd_novos_usuarios: 0,
      passagem: 0,
      ticket_ftd: 0,
      ticket_dep: 0,
      retencao: 0,
      investimento: 0,
      ggr_acumulado: 0,
      cpa_registro: 0,
      cpa_ftd: 0,
      roas_ftd: 0,
      roas_dep: 0,
      roas_ggr: 0,
      volume: 0,
    };
  }

  private calcDerivedTotals(t: UtmMetricTotals): void {
    t.passagem = t.qtd_novos_usuarios ? t.qtd_ftd / t.qtd_novos_usuarios : 0;
    t.ticket_ftd = t.qtd_ftd ? t.valor_ftd / t.qtd_ftd : 0;
    t.ticket_dep = t.qtd_depositos ? t.valor_depositos / t.qtd_depositos : 0;
    t.retencao = t.valor_ftd ? t.valor_depositos / t.valor_ftd : 0;
    t.cpa_registro = t.qtd_novos_usuarios ? t.investimento / t.qtd_novos_usuarios : 0;
    t.cpa_ftd = t.qtd_ftd ? t.investimento / t.qtd_ftd : 0;
    t.roas_ftd = t.investimento ? t.valor_ftd / t.investimento : 0;
    t.roas_dep = t.investimento ? t.valor_depositos / t.investimento : 0;
    t.roas_ggr = t.investimento ? t.ggr_acumulado / t.investimento : 0;
  }

  formatCurrency(value: number): string {
    if (value == null) return 'R$ 0,00';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatNumber(value: number): string {
    if (value == null) return '0';
    return value.toLocaleString('pt-BR');
  }

  formatPercent(value: number): string {
    if (value == null) return '0,00%';
    return (value * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }

  formatDecimal(value: number): string {
    if (value == null) return '0,00';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatXPct(value: number): string {
    if (value == null) return '0%';
    const pct = Math.round(value * 100);
    return pct.toLocaleString('pt-BR') + '%';
  }

  getCurrentSegmentName(): string {
    return this.segmentoNome || this.selectedSegmentoId || 'Nenhum segmento selecionado';
  }
}
